import React from 'react';
import styled from 'styled-components';
import { ReactComponent as FailureIcon } from '@elementary/common-ui/assets/close.svg';
import { ReactComponent as MoreHorizIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as VIcon } from '@elementary/common-ui/assets/v-green.svg';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  FlexSpace,
  IconButton,
  Menu,
} from '@elementary/common-ui/components';
import themeBase from '@elementary/common-ui/style/theme';
import { capitalize } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { Tooltip, Typography } from '@mui/material';

const STATUS = {
  CONNECTED: 'connected',
  ERROR: 'error',
};

const FailureIconStyled = styled(FailureIcon)`
  * {
    stroke: ${({ theme }) => theme.palette.common.red2};
  }
`;

const statusStylesMap = {
  [STATUS.CONNECTED]: {
    background: themeBase.palette.common.green10,
    text: themeBase.palette.common.green100,
    icon: <VIcon width={18} />,
  },
  [STATUS.ERROR]: {
    background: themeBase.palette.common.red10,
    text: themeBase.palette.common.red2,
    icon: <FailureIconStyled width={18} />,
  },
};

const ItemWrapper = styled(FlexSpace)`
  gap: 8px;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.common.gray5};
  padding: 16px;
  border-radius: 8px;
  box-sizing: border-box;
  height: 56px;
`;

const StatusChip = styled(Flex)<{ status: keyof typeof statusStylesMap }>`
  font-size: 14px;
  padding: 4px 8px;
  gap: 4px;
  border-radius: 4px;
  white-space: nowrap;
  background-color: ${({ status }) => statusStylesMap[status].background};
  color: ${({ status }) => statusStylesMap[status].text};
  font-weight: 500;
  width: fit-content;
  align-items: center;
`;

export type IntegrationDetails = {
  id: string;
  icon: React.ReactNode;
  name: string;
  status?: keyof typeof statusStylesMap;
  statusTooltip?: string | null;
  last_synced_at?: string | null;
  usageText?: string;
  customOptions?: { value: string; label: string; color?: string }[];
};

export type MultipleDestinationsRendererProps = {
  integrations: IntegrationDetails[];
  onChange: (method: string, id: string) => void;
  options: { value: string; label: string; color?: string }[];
};

const multipleDestinationsRenderer = ({
  integrations,
  onChange,
  options,
}: MultipleDestinationsRendererProps): React.ReactNode => (
  <FlexColumn sx={{ gap: '16px' }}>
    {integrations.map((integration) => {
      const lastSyncedLabel = integration.last_synced_at
        ? `Synced ${localizeDate(integration.last_synced_at).fromNow()}`
        : null;

      return (
        <ItemWrapper key={integration.id}>
          <FlexCenter sx={{ gap: '8px' }}>
            {integration.icon}
            <Typography fontSize="14px">{integration.name}</Typography>
            {integration.usageText && (
              <Typography
                fontSize="12px"
                sx={{ color: 'common.gray2', marginLeft: '8px' }}
              >
                {integration.usageText}
              </Typography>
            )}
          </FlexCenter>

          <Flex sx={{ gap: '12px', alignItems: 'center' }}>
            <Flex sx={{ gap: '8px', alignItems: 'center' }}>
              {lastSyncedLabel && (
                <Typography
                  noWrap
                  sx={{
                    fontSize: '12px',
                    color: 'common.gray2',
                  }}
                >
                  {lastSyncedLabel}
                </Typography>
              )}
              {integration.status &&
                (integration.statusTooltip ? (
                  <Tooltip
                    title={integration.statusTooltip}
                    placement="top"
                    arrow
                  >
                    <span tabIndex={0} style={{ display: 'inline-flex' }}>
                      <StatusChip status={integration.status}>
                        {statusStylesMap[integration.status].icon}
                        {capitalize(integration.status as string)}
                      </StatusChip>
                    </span>
                  </Tooltip>
                ) : (
                  <StatusChip status={integration.status}>
                    {statusStylesMap[integration.status].icon}
                    {capitalize(integration.status as string)}
                  </StatusChip>
                ))}
            </Flex>

            <Menu
              sx={{ cursor: 'pointer', marginLeft: '16px' }}
              closeOnChange
              menuButton={
                <IconButton>
                  <FlexCenter sx={{ height: '14px', width: '14px' }}>
                    <MoreHorizIcon />
                  </FlexCenter>
                </IconButton>
              }
              options={integration.customOptions || options}
              onChange={(value) => onChange(value, integration.id)}
            />
          </Flex>
        </ItemWrapper>
      );
    })}
  </FlexColumn>
);

export default multipleDestinationsRenderer;
